import { Box, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";

import {
  Checkbox,
  MenuItem,
  Slider,
  TextField,
  Typography,
} from "../../components/ui-v2";
import { GALLERY_WINDOW_Z_INDEX } from "../gallery/GalleryWindow";
import type { LightingOption } from "./types";
import { useModelViewerSettings } from "./useModelViewerSettings";
const LIGHTING_OPTIONS: LightingOption[] = [
  "DIRECTIONAL",
  "AMBIENT",
  "POINT",
] as const;

const LightingOptionToNameMap = new Map<LightingOption, string>([
  ["AMBIENT", "Ambient Illumination"],
  ["DIRECTIONAL", "Directional Light"],
  ["POINT", "Point Lighting"],
]);

const LightingOptionMaxIntensityMap = new Map<LightingOption, number>([
  ["AMBIENT", 3.0],
  ["DIRECTIONAL", 4.0],
  ["POINT", 4.0],
]);

const INITIAL_VALUE_MULTIPLIER = 0.6;

const Container = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: `0 ${theme.spacing(3)}`,
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

export const LightingControls = () => {
  const {
    lightingIntensity,
    setLightingIntensity,
    lightingType,
    setLightingType,
    showLightSource,
    setShowLightSource,
  } = useModelViewerSettings();

  const [maxIntensity, setMaxIntensity] = useState(1);

  useEffect(() => {
    const maxValue = LightingOptionMaxIntensityMap.get(lightingType) || 1.0;
    if (maxValue * INITIAL_VALUE_MULTIPLIER > lightingIntensity) {
      setLightingIntensity(maxValue * INITIAL_VALUE_MULTIPLIER);
    }
    setMaxIntensity(maxValue);
  }, [lightingType]);

  const handleChangeIntensity = (_: Event, newIntensity: number | number[]) => {
    setLightingIntensity(newIntensity as number);
  };

  const handleChangeLightingType = (event: any) => {
    setLightingType(event.target.value as LightingOption);
  };

  const handleSourceToggle = () => {
    setShowLightSource(!showLightSource);
  };

  return (
    <Container gap={2}>
      <HeaderContainer>
        <Typography color="secondary.2" variant="regular">
          Lighting
        </Typography>
      </HeaderContainer>
      <Stack
        direction="column"
        sx={{
          alignItems: "flex-start",
        }}
      >
        <Typography color="secondary.2" variant="small">
          Intensity
        </Typography>
        <Slider
          value={lightingIntensity}
          onChange={handleChangeIntensity}
          step={0.1}
          min={0}
          max={maxIntensity}
        />
      </Stack>
      <Stack
        direction="column"
        sx={{
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Typography color="secondary.2" variant="small">
          Type
        </Typography>
        <TextField
          value={lightingType}
          onChange={handleChangeLightingType}
          select
          SelectProps={{
            MenuProps: {
              sx: {
                zIndex: GALLERY_WINDOW_Z_INDEX + 1,
              },
            },
          }}
          fullWidth
          InputProps={{
            sx: {
              "&& .MuiSelect-select": {
                pr: 0,
              },
            },
          }}
        >
          {LIGHTING_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {LightingOptionToNameMap.get(option)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography color="secondary.2" variant="small">
          Show light source
        </Typography>
        <Checkbox checked={showLightSource} onClick={handleSourceToggle} />
      </Box>
    </Container>
  );
};
